import { Link, useNavigate, useParams } from "react-router-dom";
import AdminNavBar from "./components/AdminNavBar";
import { ChangeEvent, useEffect, useState } from "react";
import FetchError, { fetchErrorMessage } from "../interface/FetchError";
import { AxiosResponse } from "axios";
import Loading from "../components/LoadingComponent";
import ErrorComponent from "../components/ErrorComponent";
import apiService from "../services/api.service";

interface Book {
  id: number;
  title: string;
  author: string;
  edition: number;
  isbn: string;
  publish_status: string;
  cover_image: File | null;
}

const EditBook = () => {
  const { bookId } = useParams<{ bookId: string }>();
  const [books, setBooks] = useState<Book[]>([]);
  const [error, setError] = useState<FetchError>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleBackToSiteMaterial = () => {
    navigate("/admin/dashboard/", { state: { from: "blogposts" } });
  };

  useEffect(() => {
    const fetchBooks = async () => {
      setLoading(true);
      try {
        const bookResponse = await apiService.get<AxiosResponse>(
          `/books/?book_id=${bookId}`
        );
        setBooks(bookResponse.data);
      } catch (err) {
        if (err instanceof Error) {
          console.error(`${fetchErrorMessage(bookId, "book")}: ${err}`);
          setError(err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchBooks();
  }, []);

  const fetchedBook = books.find(
    (book: any) => book.id === parseInt(bookId || "")
  );

  const [book, setBook] = useState({
    title: "",
    author: "",
    edition: "",
    isbn: "",
    publish_status: "",
    cover_image: null as File | null,
  });

  const [currentCoverImage, setCurrentCoverImage] = useState("");

  //populate form with fetched data
  useEffect(() => {
    if (fetchedBook) {
      setCurrentCoverImage(
        fetchedBook.cover_image?.toString().split("/").pop() ||
          "No current cover image"
      );
      setBook({
        title: fetchedBook.title,
        author: fetchedBook.author,
        edition: fetchedBook.edition.toString(),
        isbn: fetchedBook.isbn,
        publish_status: fetchedBook.publish_status,
        cover_image: null,
      });
    }
  }, [fetchedBook]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setBook({
      ...book,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmitUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("title", book.title);
    formData.append("author", book.author);
    formData.append("edition", book.edition);
    formData.append("isbn", book.isbn);
    formData.append("publish_status", book.publish_status);
    if (book.cover_image) {
      formData.append("cover_image", book.cover_image, book.cover_image.name);
    }

    const makeUpdateRequest = async () => {
      try {
        await apiService.put(`/books/${bookId}/`, formData);
        alert("Book updated successfully");
      } catch (error: any) {
        console.error("Error occured while updating book", error);
        if (error.response && error.response.status === 401) {
          throw new Error("Token expired");
        }
        throw error;
      }
    };

    try {
      await makeUpdateRequest();
    } catch (error: any) {
      console.error("Error occurred while updating book", error);
      alert("Failed to update book. Please try again.");
    }
  };

  const makeDeleteRequest = async () => {
    try {
      await apiService.delete(`/books/${bookId}/`);
      alert("Book successfully deleted");
      navigate("/admin/site-materials/books");
    } catch (error: any) {
      console.error("Error occured while deleting book", error);
      if (error.response && error.response.status === 401) {
        throw new Error("Token expired");
      }
      throw error;
    }
  };

  const handleBookDelete = async () => {
    const text = "Are you sure you want to delete?";
    if (confirm(text) == true) {
      try {
        await makeDeleteRequest();
      } catch (error: any) {
        console.error("Error occurred while deleting book", error);
        alert("Failed to delete book. Please try again.");
      }
    }
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];
      setBook((prevBook) => ({
        ...prevBook,
        cover_image: file,
      }));
    }
  };

  if (loading) return <Loading />;

  if (error) return <ErrorComponent message={error.message} />;

  return (
    <>
      <nav className="navbar navbar-dark bg-dark">
        <AdminNavBar />
      </nav>
      <nav id="bc-nav" aria-label="breadcrumb">
        <ol id="bc-ol" className="breadcrumb">
          <li id="bc-books" className="breadcrumb-item">
            <button
              className="inactive-anchor-link"
              onClick={handleBackToSiteMaterial}
            >
              Site Materials
            </button>{" "}
            <span className="greater-than">&#x02AA2;</span>
          </li>
          <Link
            to={`/admin/site-materials/books`}
            className="breadcrumb-item inactive-anchor-link"
          >
            Books <span className="greater-than">&#x02AA2;</span>
          </Link>
          <li id="bc-active" className="breadcrumb-item active">
            Edit Book
          </li>
        </ol>
      </nav>
      <form id="book-form" className="mb-3" onSubmit={handleSubmitUpdate}>
        <div className="mb-3">
          <label htmlFor="book-title" className="form-label">
            {" "}
            Title
          </label>
          <input
            className={"form-control"}
            id="book-title"
            type="text"
            name="title"
            value={book.title}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Current Cover Image</label>
          <div className="current-cover-img-txt">{currentCoverImage}</div>
        </div>

        <div className="mb-3">
          <label htmlFor="formFileInput" className="form-label">
            New Cover Image
          </label>
          <input
            className={"form-control"}
            id="formFileInput"
            type="file"
            name="cover_image"
            onChange={handleImageChange}
          />
        </div>
        <div className="mb-3">
          <label className="form-label"> Author</label>
          <input
            className={"form-control"}
            type="text"
            name="author"
            value={book.author}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label"> Edition </label>
          <input
            className={"form-control"}
            type="text"
            name="edition"
            value={book.edition}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label"> ISBN </label>
          <input
            className={"form-control"}
            type="text"
            name="isbn"
            value={book.isbn}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label"> Status </label>
          <select
            className="form-select"
            name="publish_status"
            value={book.publish_status}
            onChange={handleInputChange}
            required
          >
            <option value="Pending Publish">Pending Publish</option>
            <option value="Publish"> Publish </option>
          </select>
        </div>

        <button
          id="create-post-btn"
          className="btn error-page-btn"
          type="submit"
        >
          Update Book
        </button>
      </form>

      <button
        id="delete-post-btn"
        className="btn btn-danger"
        onClick={handleBookDelete}
      >
        Delete Book
      </button>
    </>
  );
};

export default EditBook;
